import './GlobalSearchOverlay.scss';
import React, { FC, memo, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { FocusTrap, useComputedColorScheme } from '@mantine/core';
import { BorderBeam } from 'border-beam';
import { MdClose, MdOutlineBarChart, MdOutlineDescription, MdOutlineFilterList, MdOutlineMenuBook, MdOutlineSchedule, MdOutlineSearch } from 'react-icons/md';
import { IconType } from 'react-icons';
import { MdAISparkles } from '@icons';
import { IHistorySearchItem } from '@models/search/IHistorySearchItem';
import { ITabsVisibility } from '@models/search/ITabsVisibility';
import { SearchMode, SearchScope } from '@models/search/search.types';
import { SearchService } from '@services/SearchService';
import { loadTabsVisibility } from '@services/searchConfigCache';
import { ConfigService } from '@/configuration/services/configService';
import { useMorphDialog } from '@srm/ui/hooks/useMorphDialog';
import Skeleton from '@srm/ui/skeleton';
import Button from '@srm/ui/button';

export interface IGlobalSearchOverlayProps {
    /** Поле поиска в навбаре: из его геометрии панель вырастает и накрывает его. */
    anchorRef: RefObject<HTMLElement | null>;
    opened: boolean;
    /** Текущий запрос со страницы результатов — подставляется в поле панели. */
    initialText?: string;
    /** Область и режим текущей страницы результатов: панель открывается «на том же месте». */
    initialScope?: SearchScope;
    initialMode?: SearchMode;
    /** Показывать ли блок «Недавнее» (историю запросов). */
    enableSearchHistory?: boolean;
    onClose: () => void;
    /** Выполнить поиск (панель к этому моменту закрыта). */
    onSearch: (text: string, scope: SearchScope, mode: SearchMode) => void;
    /** Переход на страницу поиска с фильтрами по потоку и реквизитам. */
    onOpenAdvanced: () => void;
    /** Закрывающий морф завершён, портал размонтирован. */
    onClosed?: () => void;
}

interface IScopeItem {
    id: SearchScope;
    name: string;
    icon: IconType;
    hiddenKey: keyof ITabsVisibility;
}

const SCOPE_ITEMS: IScopeItem[] = [
    { id: 'docs', name: 'Документы', icon: MdOutlineDescription, hiddenKey: 'docsTabHidden' },
    { id: 'reports', name: 'Отчёты', icon: MdOutlineBarChart, hiddenKey: 'reportsTabHidden' },
    { id: 'dicts', name: 'Справочники', icon: MdOutlineMenuBook, hiddenKey: 'dictsTabHidden' },
];

/** AI-поиск реализован только для документов — остальные области в этом режиме недоступны. */
const AI_SCOPE: SearchScope = 'docs';

const PANEL_MARGIN = 16;
const PANEL_MIN_WIDTH = 560;
/** Левая граница развёрнутого поля — правая граница левого меню (сайдбара). */
const LEFT_MENU_SELECTOR = '.rf-folders';

interface ISearchHistoryListProps {
    history: IHistorySearchItem[];
    loading: boolean;
    onRun: (text: string) => void;
    onRemove: (event: React.MouseEvent, id: number) => Promise<void>;
}

/** «Недавнее» — memo: набор запроса в поле перерисовывает панель на каждый символ, а историю он не меняет. */
const SearchHistoryList = memo(({ history, loading, onRun, onRemove }: ISearchHistoryListProps) =>
    loading ? (
        <>
            {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="rf-gsearch-overlay__history-skeleton">
                    <Skeleton width={16} height={16} radius={4} />
                    <Skeleton width={`${62 - i * 14}%`} height={13} radius={4} />
                </div>
            ))}
        </>
    ) : (
        <>
            {history.map((item) => (
                <div key={item.id} className="rf-gsearch-overlay__history-row">
                    <button type="button" className="rf-gsearch-overlay__history-main" onClick={() => onRun(item.text)}>
                        <MdOutlineSchedule size={16} aria-hidden="true" />
                        <span>{item.text}</span>
                    </button>
                    <button
                        type="button"
                        className="rf-gsearch-overlay__history-del"
                        aria-label="Удалить из истории"
                        onClick={(event) => void onRemove(event, item.id)}
                    >
                        <MdClose size={15} aria-hidden="true" />
                    </button>
                </div>
            ))}
        </>
    ),
);
SearchHistoryList.displayName = 'SearchHistoryList';

const GlobalSearchOverlay: FC<IGlobalSearchOverlayProps> = ({
    anchorRef,
    opened,
    initialText,
    initialScope = 'docs',
    initialMode = 'exact',
    enableSearchHistory = true,
    onClose,
    onSearch,
    onOpenAdvanced,
    onClosed,
}) => {
    const config = ConfigService.get();
    const showAISearch = config.application.AI?.showAISearch ?? false;
    const aiExamples: string[] = config.application.AI?.aiSearchSwitchTooltip?.examples ?? [];

    const colorScheme = useComputedColorScheme('light');

    const [query, setQuery] = useState('');
    const [scope, setScope] = useState<SearchScope>('docs');
    const [mode, setMode] = useState<SearchMode>('exact');
    const [history, setHistory] = useState<IHistorySearchItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [tabsVisibility, setTabsVisibility] = useState<ITabsVisibility>();
    const [position, setPosition] = useState<{ top: number; left: number; width: number }>();

    const inputRef = useRef<HTMLInputElement>(null);

    const { mounted, closing, dialogRef } = useMorphDialog({
        opened,
        anchorRef,
        // Панель разворачивается сверху вниз на всю ширину, а не из угла поля.
        morphAxis: 'y',
        // Обратный scaleY сплющивает строки и контролы. На выходе контент
        // растворяется, а оболочка аккуратно подрезается к высоте поля.
        closeAnimation: 'clip',
        closeDuration: 240,
        onOpened: () => inputRef.current?.focus({ preventScroll: true }),
        onClosed,
    });

    const updatePosition = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }

        const menuEdge = document.querySelector(LEFT_MENU_SELECTOR)?.getBoundingClientRect().right;
        const right = Math.min(Math.round(rect.right), window.innerWidth - PANEL_MARGIN);
        let left = Math.max(PANEL_MARGIN, Math.round(menuEdge ?? rect.left));

        if (right - left < PANEL_MIN_WIDTH) {
            left = Math.max(PANEL_MARGIN, right - PANEL_MIN_WIDTH);
        }

        const nextPosition = {
            top: Math.max(6, rect.top - 19),
            left,
            width: Math.max(0, right - left),
        };
        setPosition((current) =>
            current?.top === nextPosition.top && current.left === nextPosition.left && current.width === nextPosition.width
                ? current
                : nextPosition,
        );
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (!opened) {
            return;
        }

        updatePosition();
        window.addEventListener('resize', updatePosition);

        const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updatePosition);
        const anchor = anchorRef.current;
        const searchContainer = anchor?.parentElement;
        const menu = document.querySelector(LEFT_MENU_SELECTOR);
        if (anchor) {
            resizeObserver?.observe(anchor);
        }
        // В шапке ширина меняется у родительского .search с transition. Наблюдаем
        // сам источник геометрии и фиксируем финальный размер по transitionend.
        if (searchContainer) {
            resizeObserver?.observe(searchContainer);
            searchContainer.addEventListener('transitionend', updatePosition);
        }
        if (menu) {
            resizeObserver?.observe(menu);
        }

        return () => {
            window.removeEventListener('resize', updatePosition);
            searchContainer?.removeEventListener('transitionend', updatePosition);
            resizeObserver?.disconnect();
        };
    }, [anchorRef, opened, updatePosition]);

    // Панель накрывает навбар: инпут встаёт на место поля («поиск раскрывается на месте»),
    // правые края поля и панели совпадают.
    useEffect(() => {
        if (!opened) {
            return;
        }
        setQuery(initialText ?? '');
        // AI работает только с документами — в этом режиме область не выбирается.
        setMode(initialMode);
        setScope(initialMode === 'ai' ? AI_SCOPE : initialScope);

        if (enableSearchHistory) {
            setHistoryLoading(true);
            void SearchService.loadHistorySearchItems()
                .then((res) => setHistory(res.data))
                .catch(() => setHistory([]))
                .finally(() => setHistoryLoading(false));
        }

        void loadTabsVisibility().then(setTabsVisibility);
        // anchorRef стабилен, initialText фиксируем на момент открытия.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    useEffect(() => {
        if (!mounted || closing) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [mounted, closing, onClose]);

    const runSearch = useCallback(
        (text: string, searchMode: SearchMode = mode) => {
            const trimmed = text.trim();
            if (!trimmed) {
                return;
            }
            onClose();
            onSearch(trimmed, searchMode === 'ai' ? AI_SCOPE : scope, searchMode);
        },
        [mode, onClose, onSearch, scope],
    );

    const onExampleClick = useCallback((example: string) => runSearch(example, 'ai'), [runSearch]);

    const onModeChange = useCallback((ai: boolean) => {
        setMode(ai ? 'ai' : 'exact');
        if (ai) {
            setScope(AI_SCOPE);
        }
        inputRef.current?.focus({ preventScroll: true });
    }, []);

    const removeHistoryItem = useCallback(async (event: React.MouseEvent, id: number) => {
        event.stopPropagation();
        try {
            await SearchService.removeHistorySearchItem(id);
            const res = await SearchService.loadHistorySearchItems();
            setHistory(res.data);
        } catch (error) {
            console.error('Failed to remove history item:', error);
        }
    }, []);

    if (!mounted) {
        return null;
    }

    const aiMode = showAISearch && mode === 'ai';
    const beamActive = aiMode && !query.trim();
    const visibleScopes = SCOPE_ITEMS.filter((item) => !tabsVisibility?.[item.hiddenKey]);

    const inputBox = (
        <div className="rf-gsearch-overlay__input">
            <MdOutlineSearch size={19} aria-hidden="true" />
            <input
                ref={inputRef}
                type="text"
                placeholder={aiMode ? 'Опишите, что ищете, — своими словами…' : 'Что ищем?'}
                aria-label="Поисковый запрос"
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        runSearch(query);
                    }
                }}
            />
            {showAISearch && (
                <div className="rf-gsearch-overlay__seg" role="group" aria-label="Режим поиска">
                    <button
                        type="button"
                        className={clsx(!aiMode && 'rf-gsearch-overlay__seg-btn--on')}
                        aria-pressed={!aiMode}
                        onClick={() => onModeChange(false)}
                    >
                        Точный
                    </button>
                    <BorderBeam
                        className={clsx('rf-gsearch-overlay__ai-beam', aiMode && 'rf-gsearch-overlay__ai-beam--on')}
                        size="sm"
                        colorVariant="ocean"
                        staticColors
                        theme={aiMode ? 'dark' : colorScheme}
                        borderRadius={7}
                        strength={aiMode ? 1 : 0.85}
                        duration={aiMode ? 1.8 : 3.2}
                        active={!closing}
                    >
                        <button
                            type="button"
                            className={clsx('rf-gsearch-overlay__seg-btn--ai', aiMode && 'rf-gsearch-overlay__seg-btn--on')}
                            aria-pressed={aiMode}
                            onClick={() => onModeChange(true)}
                        >
                            <MdAISparkles size={14} aria-hidden="true" />
                            AI
                        </button>
                    </BorderBeam>
                </div>
            )}
        </div>
    );

    return createPortal(
        <div className={clsx('rf-gsearch-overlay', closing && 'rf-gsearch-overlay--closing')}>
            <div className="rf-gsearch-overlay__scrim" onClick={onClose} aria-hidden="true" />
            <FocusTrap active={!closing} innerRef={dialogRef}>
                <div
                    className={clsx('rf-gsearch-overlay__panel', aiMode && 'rf-gsearch-overlay__panel--ai')}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Глобальный поиск"
                    style={position}
                >
                    {/* border-beam — «AI ждёт ввода»: луч бежит по рамке, пока поле пустое.
                        Обёртка либы режет box-shadow (overflow:hidden), поэтому ставится
                        только в AI-режиме, а фокус там рисуется рамкой, не кольцом. */}
                    {aiMode ? (
                        <BorderBeam
                            className="rf-gsearch-overlay__beam"
                            size="line"
                            colorVariant="colorful"
                            theme={colorScheme}
                            borderRadius={12}
                            active={beamActive}
                        >
                            {inputBox}
                        </BorderBeam>
                    ) : (
                        inputBox
                    )}

                    {visibleScopes.length > 1 && (
                        <div className="rf-gsearch-overlay__scopes">
                            <span className="rf-gsearch-overlay__scopes-label">Искать в</span>
                            {visibleScopes.map((item) => {
                                const ScopeIcon = item.icon;
                                // В AI-режиме выбор области выключен: поиск по смыслу
                                // реализован только для документов.
                                const disabled = aiMode && item.id !== AI_SCOPE;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        disabled={disabled}
                                        title={disabled ? 'AI-поиск доступен только для документов' : undefined}
                                        className={clsx(
                                            'rf-gsearch-overlay__chip',
                                            scope === item.id && 'rf-gsearch-overlay__chip--on',
                                            disabled && 'rf-gsearch-overlay__chip--disabled',
                                        )}
                                        onClick={() => setScope(item.id)}
                                    >
                                        <ScopeIcon size={16} aria-hidden="true" />
                                        {item.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className="rf-gsearch-overlay__body">
                        {aiMode && (
                            <div className="rf-gsearch-overlay__ai-note">
                                <MdAISparkles size={18} aria-hidden="true" />
                                <div>
                                    <strong>Поиск по смыслу</strong>
                                    AI найдёт документы с похожим контекстом, даже если в них нет точной фразы. Этот
                                    режим работает только с документами.
                                </div>
                            </div>
                        )}

                        {aiMode && aiExamples.length > 0 && (
                            <div className="rf-gsearch-overlay__section rf-gsearch-overlay__section--ai">
                                <div className="rf-gsearch-overlay__section-title">Попробуйте спросить</div>
                                <div className="rf-gsearch-overlay__examples">
                                    {aiExamples.map((example) => (
                                        <button
                                            key={example}
                                            type="button"
                                            className="rf-gsearch-overlay__example"
                                            onClick={() => onExampleClick(example)}
                                        >
                                            <MdAISparkles size={13} aria-hidden="true" />
                                            {example}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!aiMode && enableSearchHistory && (historyLoading || history.length > 0) && (
                            <div className="rf-gsearch-overlay__section">
                                <div className="rf-gsearch-overlay__section-title">Недавнее</div>
                                <SearchHistoryList
                                    history={history}
                                    loading={historyLoading}
                                    onRun={runSearch}
                                    onRemove={removeHistoryItem}
                                />
                            </div>
                        )}
                    </div>

                    <footer className="rf-gsearch-overlay__foot">
                        <button
                            type="button"
                            className="rf-gsearch-overlay__adv"
                            onClick={() => {
                                onClose();
                                onOpenAdvanced();
                            }}
                        >
                            <MdOutlineFilterList size={16} aria-hidden="true" />
                            Открыть поиск с фильтрами
                        </button>
                        <span className="rf-gsearch-overlay__keys">
                            <span>
                                <kbd>Enter</kbd> — искать
                            </span>
                            <span>
                                <kbd>Esc</kbd> — закрыть
                            </span>
                        </span>
                        <Button
                            className="rf-gsearch-overlay__submit"
                            variant="primary"
                            size="sm"
                            disabled={!query.trim()}
                            onClick={() => runSearch(query)}
                        >
                            Найти
                        </Button>
                    </footer>
                </div>
            </FocusTrap>
        </div>,
        document.body,
    );
};

export default GlobalSearchOverlay;
